import type { ValidationError } from '@angular/forms/signals';
import { ValidationErrorKey, ValidationErrorType } from '../types';

/** Built-in Signal Forms error `kind`s that carry a limit. */
const SignalFormsLimitErrorKind = {
  MinLength: 'minLength',
  MaxLength: 'maxLength',
  Min: 'min',
  Max: 'max',
} as const;
type SignalFormsLimitErrorKind =
  (typeof SignalFormsLimitErrorKind)[keyof typeof SignalFormsLimitErrorKind];

/**
 * Signal Forms name built-in errors by `kind` (`minLength`) and carry the limit on the error
 * itself, while `fr8bot-ui-validation-error` and the i18n keys under
 * `errors.validationErrors.*` still follow the Reactive Forms names (`minlength` +
 * `requiredLength`). This keeps one set of translations for both form models.
 *
 * An error that carries a `message` (a server error from `submit()`, for one) is shown as that
 * translation key instead.
 */
export function toValidationErrorType(
  error: ValidationError | undefined,
): ValidationErrorType | null {
  if (!error) {
    return null;
  }

  if (error.message) {
    return { key: error.kind, value: null, messageKey: error.message };
  }

  const limits = error as ValidationError & Partial<Record<SignalFormsLimitErrorKind, unknown>>;

  switch (error.kind) {
    case SignalFormsLimitErrorKind.MinLength:
      return { key: ValidationErrorKey.MinLength, value: { requiredLength: limits.minLength } };
    case SignalFormsLimitErrorKind.MaxLength:
      return { key: ValidationErrorKey.MaxLength, value: { requiredLength: limits.maxLength } };
    case SignalFormsLimitErrorKind.Min:
      return { key: ValidationErrorKey.Min, value: { min: limits.min } };
    case SignalFormsLimitErrorKind.Max:
      return { key: ValidationErrorKey.Max, value: { max: limits.max } };
    default:
      return { key: error.kind, value: error };
  }
}
